import { useNavigate, useParams } from "react-router-dom";
import InformationObjective from "../components/CaseStudiesDetails/InformationObjective";
import ImageHeader from "../components/Common/ImageHeader";
import CaseStudyImageGallery from "../components/CaseStudiesDetails/CaseStudyImageGallery";
import { useCaseStudy } from "../hooks/useCaseStudies";
import ExploreSection from "../components/ProductDetails/ExploreSection";

const CaseStudiesDetails = () => {
  const navigate = useNavigate();
  const { slug } = useParams(); 
  const { caseStudy, loading, error } = useCaseStudy(slug);

  const handleBreadcrumbClick = (href) => {
    if (href && href !== "#") {
      navigate(href);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <div>Loading case study...</div>
      </div>
    );
  }

  if (error || !caseStudy) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <div>Case study not found or error loading data.</div>
      </div>
    );
  }

  const enhancedCaseStudy = {
    ...caseStudy,
    
    title: caseStudy.imageHeader?.title || `Case Study ${caseStudy.id}`,
    description: caseStudy.imageHeader?.description,
    
    projectInformation: caseStudy.projectInfo?.description || "Project information not available",
    clientName: caseStudy.projectInfo?.title || "Client Name",
    
    objectives: caseStudy.Objectives || {
      title: "Project Objectives",
      objectiveItems: []
    },
    
    keyFeatures: caseStudy.keyFeatures || {
      featureTitle: "Key Features",
      featureOptions: []
    },
    
    gallery: caseStudy.galleryImages && caseStudy.galleryImages.length > 0 
      ? caseStudy.galleryImages.flatMap((item, galleryIndex) => {
          // Handle new schema where each galleryImages item can have multiple images
          if (item.images && Array.isArray(item.images)) {
            // Multiple images in the array
            return item.images.map((image, imageIndex) => ({
              url: image?.url ? `${process.env.REACT_APP_API_URL?.replace('/api', '')}${image.url}` : null,
              alt: image?.alternativeText || `Case Study Image ${galleryIndex + 1}-${imageIndex + 1}`,
              caption: image?.caption || `Project showcase ${galleryIndex + 1}-${imageIndex + 1}`
            }));
          } else if (item.images && !Array.isArray(item.images)) {
            // Single image object (backwards compatibility)
            return [{
              url: item.images?.url ? `${process.env.REACT_APP_API_URL?.replace('/api', '')}${item.images.url}` : null,
              alt: item.images?.alternativeText || `Case Study Image ${galleryIndex + 1}`,
              caption: item.images?.caption || `Project showcase ${galleryIndex + 1}`
            }];
          } else if (item.image) {
            // Old schema fallback - single image field
            return [{
              url: item.image?.url ? `${process.env.REACT_APP_API_URL?.replace('/api', '')}${item.image.url}` : null,
              alt: item.image?.alternativeText || `Case Study Image ${galleryIndex + 1}`,
              caption: item.image?.caption || `Project showcase ${galleryIndex + 1}`
            }];
          }
          return [];
        }).filter(item => item.url)
      : [],
    
    featuredImage: caseStudy.cardImage?.url 
      ? `${process.env.REACT_APP_API_URL?.replace('/api', '')}${caseStudy.cardImage.url}`
      : null,
    
    tasksCompleted: caseStudy.taskCompleted || [],
    challengesFaced: caseStudy.ChallengesFaced || [],
    results: caseStudy.Results || [],
    
    keyResults: caseStudy.keyResults || [],
    
    categories: caseStudy.categories || [],
    
    header: {
      title: caseStudy.imageHeader?.title || `Case Study ${caseStudy.id}`,
      description: caseStudy.imageHeader?.description,
      image: caseStudy.imageHeader?.headerImage?.url 
        ? `${process.env.REACT_APP_API_URL?.replace('/api', '')}${caseStudy.imageHeader.headerImage.url}`
        : '/png/case_study.png' // temporary fallback until headerImage is added to Strapi
    }
  };

  return (
    <div
      style={{
        background: "linear-gradient(135deg,rgba(0, 11, 140, 0), #c3cfe2 100%)",
        minHeight: "100vh",
      }}
    >
      
      <ImageHeader
        title={enhancedCaseStudy.header.title}
        subtitle={enhancedCaseStudy.header.description}
        backgroundImage={enhancedCaseStudy.header.image}
        breadcrumbItems={[
          { label: "Home", href: "/" },
          { label: "Case Studies", href: "/case-studies" },
          { label: enhancedCaseStudy.title, href: "#", isCurrentPage: true },
        ]}
        onBreadcrumbClick={handleBreadcrumbClick}
      />
      <InformationObjective caseStudy={enhancedCaseStudy} />

      {/* Case Study Image Gallery Section - Special Layout */}
      <CaseStudyImageGallery 
        images={enhancedCaseStudy.gallery}
        title="Project Gallery"
        description="Explore the visual journey and development process of this project"
      />
      <ExploreSection />

      {/* <Feature caseStudy={caseStudy} />
      <TasksAndChallenges caseStudy={enhancedCaseStudy} />
      <Results caseStudy={enhancedCaseStudy} /> */}
    </div>
  );
};

export default CaseStudiesDetails;
